import React, { useEffect, useMemo, useState } from "react";
// NAVIGATION
import { useNavigate } from "react-router-dom";
// ANT DESIGN COMPONENTS
import { Table, Menu, Dropdown, Space, Empty, Tag, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsis,
  faTrash,
  faLock,
  faBan,
  faEdit,
  faEye,
  faSearch,
  faFilter,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { useLazyGetRolesQuery } from "../../../redux/roles/roleAPI";
import { useGetOrganizationsLightQuery } from "../../../redux/organizations/organizationAPI";
import { updateOpenModalDeleteRole, updateRole } from "../../../redux/roles/roleSlice";
import {
  updateOpenModalTotpToken,
  updateTotpTokenIsValid,
} from "../../../redux/authentication/authenticationSlice";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
import { Permission, hasPermission } from "../../../utils/validatePermissions";
// COMPONENTS
import TableActionsMenuItem from "../../components/table/TableActionsMenuItem";
import TableColumnStatus from "../../components/table/TableColumnStatus";
import TableNoDataBox from "../../components/table/TableNoDataBox";
import TableColumnOrganizationType from "../../components/table/TableColumnOrganizationType";
import AuthenticationTOTP from "../../authentication/AuthenticationTOTP";
// FILTERS
import FilterSearchText from "../../components/filters/FilterSearchText";
import FilterSelectStatus from "../../components/filters/FilterSelectStatus";
// UTILS
import { getTableRowClass, buildFilterString } from "../../../utils/utils";
import { formatUuidT, orgTypeLabel } from "../../../flatbuffers/display";
// MASTER-ORG OWNERSHIP — shared with the wizard + the create form
import { isMasterOrgRole as isMasterOrgRoleShared } from "../../../utils/masterOrganization";
// MASTER ROLE NAME — same env + offline-fallback resolution isMasterAdmin
// relies on, used below to hide the MASTER ADMIN row from non-master-org users.
import { isMasterRoleName } from "../../../utils/isMasterAdmin";
// INTERFACES
import { RootState } from "../../../app/store";
import { RoleInterface } from "../../../interfaces/User.interface";

const RolesTable = () => {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [t] = useTranslation("global");
  const { Column } = Table;

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [roles, setRoles] = useState<RoleInterface[]>([]);
  const [selectedItem, setSelectedItem] = useState<RoleInterface>({
    name: "",
    organizationId: "",
    id: "",
    active: true,
    menu: { name: "", active: true, offline: true, pages: [] },
  });
  const [filterName, setFilterName] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  // Which action (edit/delete) — and on which role — is waiting on the
  // password+TOTP step-up challenge. Carries the role explicitly (not just
  // `selectedItem`) so this works whether step-up was triggered from the
  // Actions menu or straight off a row click; set right before opening
  // AuthenticationTOTP, consumed once totpTokenIsValid flips true,
  // mirroring UsersTable's pswType pattern (the shared authentication slice
  // carries no action-type of its own).
  const [pendingMasterOrgAction, setPendingMasterOrgAction] = useState<{
    action: "edit" | "delete";
    item: RoleInterface;
  } | null>(null);
  const [pagination, setPagination] = useState<any>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
    total: 0,
  });
  const [errorMessage, setErrorMessage] = useState<any>("");
  // Token for the light-orgs lookup (the roles payload only carries orgId, so
  // the Organization / Organization Type columns are resolved by joining
  // role.orgId against the org tree the user can see).
  const [tokenOrganizations, setTokenOrganizations] = useState<string | null>(null);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const { roleRefreshTable } = useSelector((state: RootState) => state.role);
  const { theme, globalOrganizationId, userLogged } = useSelector((state: RootState) => state.home);
  const { totpTokenIsValid } = useSelector((state: RootState) => state.authentication);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetRolesTable, { data, isLoading, isError, isFetching, error }] =
    useLazyGetRolesQuery();

  // Light org list (id + name + type) for the whole visible org tree, used to
  // resolve the role's owning organization name/type for display.
  const { data: dataOrganizationsLight } = useGetOrganizationsLightQuery(
    { token: tokenOrganizations },
    { skip: !tokenOrganizations, refetchOnMountOrArgChange: false },
  );

  // Map<orgId string, { name, type }> built from the light org list.
  const orgById = useMemo(() => {
    const map = new Map<string, { name: string; type: string }>();
    (dataOrganizationsLight?.data ?? []).forEach((organization: any) => {
      const id = formatUuidT(organization?.id);
      if (!id) return;
      map.set(id, {
        name: String(organization?.name ?? "").trim(),
        type: organization?.type != null ? orgTypeLabel(organization.type) : "",
      });
    });
    return map;
  }, [dataOrganizationsLight]);

  // Resolves the Organization / Organization Type columns for one role.
  //
  // The two endpoints this table joins are scoped DIFFERENTLY on the server,
  // and they differ by exactly one organization:
  //   GET /roles/      → ReachableOrgIDsWithMaster — the caller's org tree
  //                      UNIONED with the master org's own id, because LiftNet
  //                      owns the role templates tenant roles inherit through
  //                      base_role_id, so its roles are listed for everyone.
  //   GET /orgs/light/ → ReachableOrgIDs — the caller's org tree ONLY. It is
  //                      the org SWITCHER, so it deliberately does not offer
  //                      LiftNet to a tenant.
  // A user outside the master organization therefore receives the master-org
  // ROLES but never a master-org ENTRY in the light list: the join missed and
  // both columns rendered blank for exactly those rows (a master admin is
  // seeded INTO the master org, so its id is already inside their own subtree
  // and they never saw it). Resolve that one id from the same build-time
  // constants isMasterOrgRole already relies on, rather than widening
  // /orgs/light/ — that would put LiftNet in every tenant's org switcher.
  //
  // REACT_APP_MASTER_ORG_NAME is only set in .env.test — every other env file
  // (local/dev/production/offline/wizard) leaves it undefined, so the name
  // fell back to "" and the column rendered blank instead of falling back at
  // all. "LiftNet" is a hardcoded last resort so the column always shows
  // something for the master-org row regardless of which env is deployed.
  const resolveOrg = (record: RoleInterface) => {
    const id = formatUuidT(record?.orgId);
    const fromLightList = orgById.get(id);
    if (fromLightList) return fromLightList;
    if (id && id === process.env.REACT_APP_MASTER_ORG_ID) {
      return {
        name: process.env.REACT_APP_MASTER_ORG_NAME || "LiftNet",
        type: process.env.REACT_APP_ORGANIZATION_MASTER_TYPE ?? "",
      };
    }
    return undefined;
  };

  // ************************************************ */
  // FUNCTIONS ************************************** */
  // Roles owned by the master organization are LiftNet-level roles — never
  // gate them behind casual edit/delete, only after a fresh password+TOTP
  // step-up (same check already used for the "Locked" status tag below).
  const isMasterOrgRole = (item: RoleInterface) => isMasterOrgRoleShared(item);

  // Only a user who themselves belongs to the master organization may
  // edit/delete a master-org role at all — everyone else must not see those
  // actions for that role, step-up or no step-up.
  const isLoggedUserInMasterOrg =
    userLogged?.organizationId === process.env.REACT_APP_MASTER_ORG_ID;

  const canManageRole = (item: RoleInterface) => !isMasterOrgRole(item) || isLoggedUserInMasterOrg;

  // Read-only permissions page for a master-organization role. The role row is
  // passed along in router state as a fallback source: GET /roles/ lists
  // master-org roles to every tenant, while the single-role GET the view page
  // prefers is not guaranteed to be scoped that widely.
  const handleViewRole = (item: RoleInterface) => {
    navigate(`/organization-management/roles/view/${formatUuidT(item.id)}`, {
      state: { role: item },
    });
  };

  const handleOpenModalDelete = () => {
    dispatch(updateRole(selectedItem));
    dispatch(updateOpenModalDeleteRole(true));
  };

  // Dedicated full-page edit route — same destination the master-org step-up
  // flow already navigated to. Every role now edits here; the inline
  // RolesEditRoleForm drawer stays in the codebase (RolesPage.tsx still
  // mounts it for RolesStaticRolesTree's own edit entry point) but this
  // table no longer opens it.
  const handleEditRole = (item: RoleInterface) => {
    navigate(`/organization-management/roles/edit/${formatUuidT(item.id)}`);
  };

  // Defers edit/delete behind the shared password+TOTP step-up modal — it
  // carries no action-type of its own, so which action (and on which role)
  // is pending is kept here and consumed once totpTokenIsValid flips (see
  // the effect below).
  const requireStepUpThen = (item: RoleInterface, action: "edit" | "delete") => {
    dispatch(updateRole(item));
    setPendingMasterOrgAction({ action, item });
    dispatch(updateOpenModalTotpToken(true));
  };

  const onCellClick = (record: RoleInterface) => {
    if (!record) return;
    // A user outside the master organization gets no edit access to a
    // master-org role at all — not even behind step-up. The row-click
    // shortcut must respect that the same way the Actions menu does.
    if (!canManageRole(record)) return;
    // A master-org role's row-click shortcut must go through the same
    // step-up gate as the Actions menu's Edit item — otherwise clicking the
    // row would bypass it entirely.
    if (isMasterOrgRole(record)) {
      requireStepUpThen(record, "edit");
      return;
    }
    // Every other role's row-click shortcut goes to the same full-page edit
    // route as the Actions menu's Edit item.
    handleEditRole(record);
  };

  const getData = async (page: number, limit: number) => {
    let idOrganization = globalOrganizationId;
    if (userLogged && userLogged.organizationId && userLogged.organizationId === idOrganization) {
      idOrganization = "";
    }
    const searchAndFilterString = buildFilterString({
      filterName,
      filterOrganizationFullName: idOrganization,
      filterStatus,
    });
    const token = await GETJwtToken();
    triggerGetRolesTable({
      page,
      limit,
      token,
      searchAndFilterString,
    });
  };

  const handleTableChange = async (pagination_: any) => {
    setPagination({
      ...pagination,
      current: pagination_.current,
      pageSize: pagination_.pageSize,
      total: pagination_.total,
    });
    getData(pagination_.current, pagination_.pageSize);
  };

  const refreshTable = () => {
    setFilterName("");
    setFilterStatus("");
    setPagination({
      ...pagination,
      current: 1,
      pageSize: 10,
    });
    getData(1, 10);
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    getData(1, 10);
    // Fetch a token once so the light-orgs lookup can resolve the org column.
    (async () => {
      setTokenOrganizations(await GETJwtToken());
    })();
  }, []);

  // FRONTEND-ONLY visual filter: a user outside the master organization never
  // sees the MASTER ADMIN row at all — not just gated behind step-up, hidden
  // entirely, since that role has no relevance outside LiftNet. Deliberately
  // NOT done server-side: GET /roles/ has no query param for it today, and
  // hiding it there would need a LiftCloud change outside this repo. Known
  // trade-off: this table is server-paginated (pagination.total, page fill
  // come from the server), so removing a row client-side can leave the
  // "X Roles" footer count and a page's row count off by the number of
  // MASTER ADMIN rows hidden from it — accepted for now, same class of
  // page-local-only limitation as the master-first sort below.
  const visibleRoles = useMemo(
    () => (isLoggedUserInMasterOrg ? roles : roles.filter((role) => !isMasterRoleName(role.name))),
    [roles, isLoggedUserInMasterOrg],
  );

  // LiftNet/MASTER roles surface first within the loaded page — Array#sort
  // is stable, so the server's original order is kept within each group
  // (master / non-master). This table is server-paginated, so the sort only
  // reorders the CURRENT page's rows, same as every other client-side
  // affordance here (filters, etc.) — it cannot reach across pages.
  const sortedRoles = useMemo(
    () => [...visibleRoles].sort((a, b) => Number(isMasterOrgRole(b)) - Number(isMasterOrgRole(a))),
    [visibleRoles],
  );

  useEffect(() => {
    if (data && data.data) {
      setPagination({
        ...pagination,
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: data.total,
      });
      setRoles(data.data);
    }
  }, [data]);

  useEffect(() => {
    if (isError) {
      setRoles([]);
      setPagination({
        ...pagination,
        current: 1,
        pageSize: 10,
        total: 0,
      });
    }
  }, [isError]);

  useEffect(() => {
    if (roleRefreshTable) {
      refreshTable();
    }
  }, [roleRefreshTable]);

  // Fires the deferred edit/delete once the password+TOTP challenge passes,
  // then resets totpTokenIsValid — same setTimeout convention UsersTable /
  // ServersTable already use so a stale "valid" flag can't leak into an
  // unrelated later step-up prompt. A master-org role's "edit" goes to the
  // same dedicated full-page edit route every other role uses (organization
  // locked, no sub-organization picker there) — step-up only gates reaching
  // it, not the destination.
  useEffect(() => {
    if (totpTokenIsValid && pendingMasterOrgAction) {
      const { action, item } = pendingMasterOrgAction;
      if (action === "edit") {
        handleEditRole(item);
      } else {
        dispatch(updateRole(item));
        dispatch(updateOpenModalDeleteRole(true));
      }
      setPendingMasterOrgAction(null);
      setTimeout(() => {
        dispatch(updateTotpTokenIsValid(false));
      }, 1000);
    }
  }, [totpTokenIsValid]);

  useEffect(() => {
    if (error) {
      setErrorMessage(error);
    }
  }, [error]);

  useEffect(() => {
    if (globalOrganizationId !== "") {
      getData(1, 10);
    }
  }, [globalOrganizationId]);

  // ************************************************ */
  // ROW ACTIONS ************************************ */
  const manageActions = () => {
    const BLANK = {
      label: (
        <div className="generalStyles__tableActionBox">
          <span>
            <FontAwesomeIcon className="generalStyles__error generalStyles__mrFix" icon={faBan} />
            {t("general.noActions")}
          </span>
        </div>
      ),
      key: "1",
    };

    const isSelectedMasterOrgRole = isMasterOrgRole(selectedItem);
    const canManageSelectedRole = canManageRole(selectedItem);
    // The literal MASTER ADMIN role stays fully locked — no Edit, no
    // Delete — same guard the pre-existing BLANK-menu gate below enforced.
    // View is exempt from this lock: it's read-only, gated by RolesView the
    // same as every other role's View entry.
    const isLiteralMasterAdminRole = selectedItem?.name === process.env.REACT_APP_ROLE_MASTER_NAME;

    // Every role gets a read-only View entry — anyone who may list roles may
    // also look at what one grants, even when they may not touch it (the
    // edit/delete gate below is unchanged). Previously this was offered only
    // for LiftNet/MASTER-owned template roles; tenant-owned roles now get
    // the same entry alongside their existing edit-in-place flow.
    const VIEW = hasPermission(Permission.RolesView, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faEye}
              text={t("organizationManagement.view")}
              onClick={() => handleViewRole(selectedItem)}
            />
          ),
          key: "0",
        }
      : null;

    const EDIT =
      !isLiteralMasterAdminRole &&
      hasPermission(Permission.RolesUpdate, permissionSet ?? []) &&
      canManageSelectedRole
        ? {
            label: (
              <TableActionsMenuItem
                icon={faEdit}
                text={t("organizationManagement.edit")}
                onClick={
                  isSelectedMasterOrgRole
                    ? () => requireStepUpThen(selectedItem, "edit")
                    : () => handleEditRole(selectedItem)
                }
              />
            ),
            key: "1",
          }
        : null;

    const DELETE =
      !isLiteralMasterAdminRole &&
      hasPermission(Permission.RolesDelete, permissionSet ?? []) &&
      canManageSelectedRole
        ? {
            label: (
              <TableActionsMenuItem
                icon={faTrash}
                text={t("organizationManagement.delete")}
                onClick={
                  isSelectedMasterOrgRole
                    ? () => requireStepUpThen(selectedItem, "delete")
                    : handleOpenModalDelete
                }
              />
            ),
            key: "3",
          }
        : null;

    // RETURN OBJECT WITH VALID ACTIONS
    if (selectedItem && (VIEW || EDIT || DELETE)) {
      return [VIEW, EDIT, DELETE];
    }
    return [BLANK];
  };

  const menu = <Menu items={manageActions()} />;

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className="mt-3">
      {hasPermission(Permission.RolesView, permissionSet ?? []) ? (
        <Table
          locale={{
            emptyText: (
              <TableNoDataBox
                isError={isError}
                errorMessage={
                  errorMessage && errorMessage.data && errorMessage.data.message
                    ? errorMessage.data.message
                    : "Error"
                }
                noItemsFound="No roles found"
              />
            ),
          }}
          rowClassName={(record, index) => getTableRowClass(index, theme)}
          className="customTable"
          dataSource={sortedRoles}
          loading={isLoading || isFetching}
          rowKey={(record: any) => formatUuidT(record.id)}
          size="small"
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 900 }}
          footer={() => (
            <div className="generalStyles__flexEnd">
              <Tag>{pagination.total} Roles</Tag>
            </div>
          )}
        >
          {/** DESCRIPTION */}
          <Column
            title={t("organizationManagement.description") || "Description"}
            key="description"
            width="60px"
            align="center"
            render={() => (
              // TODO: today's role payload has no description field — this
              // is a placeholder tooltip until a future instruction wires
              // real data into it (see roleMenu.ts's getDescription for the
              // same forward-compatible pattern on the permissions cards).
              <Tooltip title="">
                <FontAwesomeIcon icon={faInfoCircle} style={{ color: "#2968c0" }} />
              </Tooltip>
            )}
          />
          {/** NAME */}
          <Column
            onCell={(record: RoleInterface) => ({
              onClick: () => {
                onCellClick(record);
              },
            })}
            title={t("general.name")}
            dataIndex="name"
            key="name"
            width="200px"
            className="generalStyles__hoverCursor"
            filterIcon={<FontAwesomeIcon icon={faSearch} />}
            filterDropdown={() => (
              <FilterSearchText
                placeHolder={t("general.name")}
                filterValue={filterName}
                setFilterValue={setFilterName}
                refreshTable={refreshTable}
                refreshParameters={[]}
              />
            )}
            render={(text) => <div className="generalStyles__ellipsis__200px">{text}</div>}
          />
          {/** ORGANIZATION */}
          <Column
            onCell={(record: RoleInterface) => ({
              onClick: () => {
                onCellClick(record);
              },
            })}
            className="generalStyles__hoverCursor"
            title={t("organizationManagement.organization")}
            dataIndex="organization"
            key="organization"
            width="200px"
            render={(text, record: RoleInterface) => <div>{resolveOrg(record)?.name ?? ""}</div>}
          />
          {/** ORGANIZATION - TYPE */}
          <Column
            title={t("general.organizationType")}
            dataIndex="organizationType"
            key="organizationType"
            width="200px"
            className="generalStyles__hoverCursor"
            render={(text, record: RoleInterface) => (
              <TableColumnOrganizationType type={resolveOrg(record)?.type ?? ""} />
            )}
          />
          {/** STATUS */}
          <Column
            onCell={(record: RoleInterface) => ({
              onClick: () => {
                onCellClick(record);
              },
            })}
            title={t("organizationManagement.status")}
            dataIndex="status"
            key="status"
            width="200px"
            align="center"
            className="generalStyles__hoverCursor"
            filterIcon={<FontAwesomeIcon icon={faFilter} />}
            filterDropdown={() => (
              <FilterSelectStatus
                placeHolder={t("general.status")}
                setFilterValue={setFilterStatus}
                refreshTable={refreshTable}
                filterValue={filterStatus}
                refreshParameters={[]}
              />
            )}
            render={(text, record: RoleInterface) => (
              <div className="generalStyles__flexCenter">
                {isMasterOrgRole(record) ? (
                  <Tag
                    color="error"
                    icon={<FontAwesomeIcon icon={faLock} className="generalStyles__mrFix" />}
                    className="drawer__tag"
                  >
                    {t("general.locked")}
                  </Tag>
                ) : (
                  <TableColumnStatus active={record.active} />
                )}
              </div>
            )}
          />
          {/** ACTIONS */}
          <Column
            title={t("organizationManagement.actions")}
            key="action"
            width="50px"
            render={(_: any, record: RoleInterface) => (
              <Dropdown overlay={menu} trigger={["click"]}>
                <Space
                  className="generalStyles__hoverCursor"
                  onClick={() => {
                    setSelectedItem(record);
                  }}
                >
                  <FontAwesomeIcon icon={faEllipsis} />
                </Space>
              </Dropdown>
            )}
          />
        </Table>
      ) : (
        <div className="generalStyles__noAccessToListTable">
          <Empty
            description={t("organizationManagement.listNotAvailableRoles")}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      )}
      {/** ********************************************* */}
      {/** AUTHENTICATION TOTP TOKEN — password+TOTP step-up gate for
          edit/delete on a master-organization role */}
      <AuthenticationTOTP />
    </div>
  );
};

export default RolesTable;
